import type { MultipleChoiceQuestion, RatingQuestion, TextQuestion } from '@shared/domain/form';
import type { IntelligenceRequest } from '@shared/domain/intelligenceRequest/IntelligenceRequest';
import type { BasicReview } from '@shared/domain/review';
import type { RatingsInput, RatingValues } from '../../api/api';
import { Button } from '@mui/material';
import { QuestionType } from '@shared/domain/form';
import { IntelligenceRequestStatus } from '@shared/domain/intelligenceRequest/IntelligenceRequestStatus';
import { ReviewDepth, ReviewStep } from '@shared/domain/review';
import { useIntelligenceRequests } from '@shared/hooks/useIntelligenceRequests';
import { useApi } from '@shared/services/api';
import { useExtendState } from '@shared/utils/ExtendState';
import { log } from '@shared/utils/log';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserContext } from '@/shared/contexts/UserContext';
import { createBasicReview, createBasicReviewRatings, createCompanyFromReview } from '../../api/api';
import { useReviewContext } from '../../context/ReviewContext';
import { BASIC_REVIEW_FORM_FIELDS } from '../../data';
import { useLegalCheck } from '../../hooks/useLegalCheck';
import useValidation from '../../hooks/useValidation';
import ActiveReviewIntelligenceRequests from '../ActiveReviewIntelligenceRequests';
import AddCompanyForm from '../AddCompanyForm';
import MultipleChoiceReviewQuestion from '../question_types/ReviewMultipleChoiceQuestion';
import ReviewRatingQuestion from '../question_types/ReviewRatingQuestion';
import ReviewTextArea from '../question_types/ReviewTextQuestion';
import '../../styles/review-form.scss';

function toNumeric(val: any): number {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}

function mapRatingsArray(ratings?: Array<{ type?: string; rating?: string | number }>): RatingValues {
  const out: RatingValues = {
    transparency: 0,
    clarity: 0,
    responsiveness: 0,
    understanding: 0,
  };
  if (!Array.isArray(ratings)) return out;
  for (const r of ratings) {
    const key = (r?.type || '').toString().toLowerCase();
    if (key in out) {
      // @ts-expect-error index by known keys
      out[key] = toNumeric(r?.rating);
    }
  }
  return out;
}

interface BasicReviewTypeProps {
  companyId?: number;
  companyName?: string;
  cancelReview?: () => void;
  handleReviewSuccess?: (showSnackbar: boolean, redirectPath?: string) => void;
  handleEditSuccess?: () => void;
  setRfp?: (rfp: boolean) => void;
  isAddCompany?: boolean;
  editMode?: boolean;
  defaultValues?: Partial<BasicReview & RatingValues>;
  seedLegalCheckFlags?: Record<string, import('../../legal-check/types').LegalCheckFlag[]>;
}

function getDisabledOptions(questionName: string, selectedRequest: IntelligenceRequest | null): string[] | undefined {
  if (questionName === 'dealArea') {
    return selectedRequest?.dealArea && selectedRequest.dealArea.length > 0 ? selectedRequest.dealArea : undefined;
  }
  if (questionName === 'dealClose') {
    const outcome = selectedRequest?.dealOutcome;
    return outcome ? [outcome] : undefined;
  }
  return undefined;
}

const BasicReviewForm: React.FC<BasicReviewTypeProps> = ({
  companyId,
  companyName,
  setRfp,
  isAddCompany,
  cancelReview,
  handleReviewSuccess,
  editMode,
  defaultValues,
  handleEditSuccess,
  seedLegalCheckFlags,
}) => {
  const { fetchFilteredReviews, currentReview, setReviewId, setReviewStep, reviewId } = useReviewContext();
  const { user } = useContext(UserContext);
  const { intelligenceRequests, getIntelligenceRequests } = useIntelligenceRequests();
  const [selectedIntelligenceRequest, setSelectedIntelligenceRequest] = useState<number | null>(null);

  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();

  const selectedRequest = useMemo(
    () => intelligenceRequests.find((req) => Number(req.id) === selectedIntelligenceRequest) || null,
    [intelligenceRequests, selectedIntelligenceRequest],
  );

  const ratingValues = useMemo<RatingValues>(() => {
    return mapRatingsArray(currentReview?.ratings as any);
  }, [currentReview?.ratings]);

  const [formFields, extendFormFields, setFormFields] = useExtendState<BasicReview>(
    editMode && defaultValues ? (defaultValues as BasicReview) : (currentReview ?? ({} as BasicReview)),
  );

  const [ratingFormFields, setRatingFormFields] = useState<RatingValues>(
    editMode && defaultValues
      ? {
          transparency: defaultValues.transparency ?? 0,
          clarity: defaultValues.clarity ?? 0,
          responsiveness: defaultValues.responsiveness ?? 0,
          understanding: defaultValues.understanding ?? 0,
        }
      : ratingValues,
  );

  const [newCompany, setNewCompany] = useState({
    companyName: companyName || '',
    stockTicker: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { errors, validateFields, validateField } = useValidation(
    { ...formFields, ...ratingFormFields },
    BASIC_REVIEW_FORM_FIELDS,
  );

  const { getLegalCheckBindings } = useLegalCheck({
    questionFields: BASIC_REVIEW_FORM_FIELDS,
    seedFlags: seedLegalCheckFlags,
  });

  const postReviewSteps = (
    isReviewDeepDive: boolean,
    companyId: number,
    handleReviewSuccess: (showSnackbar: boolean, redirectPath?: string) => void,
    fetchAllReviews: (companyId: number) => void,
    createdCompanyName?: string,
  ) => {
    if (isReviewDeepDive) {
      setReviewStep(1);
    } else {
      fetchAllReviews(companyId);
      if (isAddCompany && createdCompanyName) {
        handleReviewSuccess(true, `/company/${encodeURIComponent(createdCompanyName)}`);
      } else {
        handleReviewSuccess(true);
      }
    }
  };

  const handleRatingChange = (name: string, value: number | null) => {
    setRatingFormFields({
      ...ratingFormFields,
      [name]: value || 0,
    });
    validateField(name, value);
  };

  const submitBasicReviewType = async (isReviewDeepDive?: boolean) => {
    if (isSubmitting) {
      return;
    }

    const hasErrors = validateFields();
    if (!user?.idToken || hasErrors) {
      return;
    }

    setIsSubmitting(true);

    try {
      // If reviewId already exists, just navigate forward without creating a new review
      if (reviewId) {
        postReviewSteps(
          isReviewDeepDive || false,
          companyId as number,
          handleReviewSuccess ?? (() => {}),
          () =>
            fetchFilteredReviews({
              searchType: 'custom',
              companyId: companyId as number,
            }),
          companyName,
        );
        setIsSubmitting(false);
        return;
      }

      const createdCompany = isAddCompany ? await createCompanyFromReview(newCompany, user?.idToken) : null;

      const reviewInput = {
        ...formFields,
        reviewType: 'OWNED',
        reviewDepth: currentReview?.reviewDepth || isReviewDeepDive ? ReviewDepth.DEEP_DIVE : ReviewDepth.BASIC,
        userId: user?.id,
        companyId: createdCompany?.id ?? companyId ?? 0,
        companyName: createdCompany?.companyName || companyName,
        key: ReviewStep.BASIC_REVIEW,
      };

      const createdBasicReviewId = await createBasicReview(reviewInput, user?.idToken);

      setReviewId(createdBasicReviewId);

      const ratingsInput: RatingsInput = {
        companyId: createdCompany?.id ?? companyId ?? 0,
        reviewId: createdBasicReviewId,
        ratings: {
          transparency: ratingFormFields.transparency,
          clarity: ratingFormFields.clarity,
          responsiveness: ratingFormFields.responsiveness,
          understanding: ratingFormFields.understanding,
        },
      };

      await createBasicReviewRatings(ratingsInput, user?.idToken);

      postReviewSteps(
        isReviewDeepDive || false,
        (createdCompany?.id ?? companyId) as number,
        handleReviewSuccess ?? (() => {}),
        () =>
          fetchFilteredReviews({
            searchType: 'custom',
            companyId: (createdCompany?.id ?? companyId) as number,
          }),
        createdCompany?.companyName,
      );
      setFormFields({} as BasicReview);
    } catch (error) {
      log.error('Error submitting basic review:', error);
      setIsSubmitting(false);
      if (cancelReview) {
        cancelReview();
      }
    }
  };

  const editReview = async (reviewId: number, fields: BasicReview): Promise<void> => {
    const hasErrors = validateFields();
    if (hasErrors) {
      return;
    }

    await api.patch(`/review/${reviewId}/edit`, {
      reviewId,
      data: fields,
      step: ReviewStep.BASIC_REVIEW,
      isAdmin: user?.isAdmin,
    });
    handleEditSuccess && handleEditSuccess();
  };

  const handleCustomSubmit = () => {
    submitBasicReviewType(true);
  };

  const handleCustomEdit = () => {
    if (defaultValues?.id) {
      editReview(defaultValues.id, {
        ...formFields,
        ...ratingFormFields,
      });
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (formFields.aiInvolved === 'Yes') {
      searchParams.set('showAiQuestion', 'true');
    } else {
      searchParams.delete('showAiQuestion');
    }
    navigate({ search: searchParams.toString() }, { replace: true });
  }, [formFields.aiInvolved, location.search, navigate]);

  useEffect(() => {
    getIntelligenceRequests({
      status: [IntelligenceRequestStatus.ACTIVE],
      companyId: [companyId],
    });
  }, [companyId]);

  useEffect(() => {
    if (setRfp) {
      setRfp(formFields.dealRfp === 'Yes');
    }
  }, [formFields.dealRfp, setRfp]);

  useEffect(() => {
    if (editMode) {
      setFormFields(defaultValues as BasicReview);
    } else {
      setFormFields(currentReview ?? ({} as BasicReview));
    }
  }, [currentReview, editMode]);

  useEffect(() => {
    if (editMode) {
      setRatingFormFields({
        transparency: defaultValues?.transparency ?? 0,
        clarity: defaultValues?.clarity ?? 0,
        responsiveness: defaultValues?.responsiveness ?? 0,
        understanding: defaultValues?.understanding ?? 0,
      });
    } else {
      setRatingFormFields(ratingValues);
    }
  }, [ratingValues, editMode, defaultValues]);

  const multipleChoiceQuestions = BASIC_REVIEW_FORM_FIELDS.filter(
    (q): q is MultipleChoiceQuestion => q.type === QuestionType.MULTIPLE_CHOICE,
  );
  const textQuestions = BASIC_REVIEW_FORM_FIELDS.filter((q): q is TextQuestion => q.type === QuestionType.TEXT);
  const ratingQuestions = BASIC_REVIEW_FORM_FIELDS.filter((r): r is RatingQuestion => r.type === QuestionType.RATING);

  return (
    <div className="review-page">
      <header className="review-page__header">
        <p className="review-page__eyebrow">Step 1 · Deal review</p>
        <h1 className="review-page__title">{editMode ? 'Edit your deal review' : 'Tell us about this deal'}</h1>
        <p className="review-page__subtitle">
          Share what you learned. Future sellers walking into this account will use your notes to skip what didn't work
          and double down on what did.
        </p>
      </header>

      {!editMode && (
        <aside className="review-info-bar" data-testid="basic-review-step-content-anchor">
          <h3>How to write a great review</h3>
          <ul>
            <li>
              <strong>Take your time.</strong> 15–20 minutes of detail is more useful than a 5-minute summary. If you're
              flying through, you're probably not saying enough.
            </li>
            <li>
              <strong>Empathize.</strong> Put yourself in the shoes of the next AE walking into this account — what
              would have helped you?
            </li>
            <li>
              <strong>Be specific.</strong> "Know your champion" isn't useful. Explain what worked{' '}
              <em>here, on this account.</em>
            </li>
          </ul>
        </aside>
      )}

      {!editMode && (
        <ActiveReviewIntelligenceRequests
          companyId={companyId ?? 0}
          extendReview={extendFormFields}
          selectedId={selectedIntelligenceRequest}
          setSelectedId={setSelectedIntelligenceRequest}
          intelligenceRequests={intelligenceRequests}
        />
      )}

      {isAddCompany && <AddCompanyForm newCompany={newCompany} setNewCompany={setNewCompany} errors={errors} />}

      <form>
        {multipleChoiceQuestions.length > 0 && (
          <section className="review-section">
            <header className="review-section__heading">
              <h2 className="review-section__title">Quick facts</h2>
              <p className="review-section__hint">A few one-tap selections to set the scene.</p>
            </header>
            <div className="review-mcq-list">
              {multipleChoiceQuestions.map((question) => (
                <MultipleChoiceReviewQuestion
                  key={question.name}
                  question={question}
                  disabledOptions={getDisabledOptions(question.name, selectedRequest)}
                  formFields={formFields}
                  handleFormFieldChange={(e) => {
                    extendFormFields({ [question.name]: e.currentTarget.value });
                    validateField(question.name, e.currentTarget.value);
                  }}
                  errors={errors}
                  isNarrow={question.options.length > 8}
                />
              ))}
            </div>
          </section>
        )}

        {textQuestions.length > 0 && (
          <section className="review-section">
            <header className="review-section__heading">
              <h2 className="review-section__title">What happened</h2>
              <p className="review-section__hint">Tell the story. Generalize anything sensitive.</p>
            </header>
            <div className="review-text-grid">
              {textQuestions.map((question, i) => (
                <ReviewTextArea
                  key={question.name}
                  question={question}
                  formFields={formFields}
                  handleFormFieldChange={(e) => {
                    extendFormFields({ [question.name]: e.currentTarget.value });
                    validateField(question.name, e.currentTarget.value);
                  }}
                  errors={errors}
                  includeTextLimitValidation
                  index={i}
                  helperText={question.helperText}
                  legalCheck={getLegalCheckBindings(question.name)}
                />
              ))}
            </div>
          </section>
        )}

        {ratingQuestions.length > 0 && (
          <section className="review-section">
            <header className="review-section__heading">
              <h2 className="review-section__title">How they showed up</h2>
              <p className="review-section__hint">
                Rate the buying side from 1 (low) to 5 (high) across these dimensions.
              </p>
            </header>
            <div className="review-ratings-grid">
              {ratingQuestions.map((question) => (
                <ReviewRatingQuestion
                  handleRatingChange={handleRatingChange}
                  key={question.name}
                  disabled={editMode}
                  question={question}
                  formFields={ratingFormFields}
                  currentReview={currentReview}
                  errors={errors}
                />
              ))}
            </div>
          </section>
        )}
      </form>

      <div className="review-save-bar">
        <Button
          variant="contained"
          className="review-save-bar__button"
          data-testid="review-save-button"
          disabled={isSubmitting}
          onClick={() => {
            if (editMode) {
              handleCustomEdit();
            } else {
              submitBasicReviewType(true);
            }
          }}
        >
          {isSubmitting ? 'Submitting…' : editMode ? 'Save changes' : 'Continue to Entry Strategy'}
        </Button>
      </div>
    </div>
  );
};

export default BasicReviewForm;
